import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiOutlineBuildingStorefront, HiOutlinePlusCircle, HiOutlineMapPin, HiOutlineCog6Tooth, HiOutlineArrowRight } from 'react-icons/hi2';
import { useAuth } from '../context/AuthContext';
import { useRestaurant } from '../context/RestaurantContext';
import TopBar from '../components/TopBar';

const RestaurantSelector = () => {
  const nav = useNavigate();
  const { user } = useAuth();
  const { restaurants, loading, selectRestaurant } = useRestaurant();

  // Whenever the owner lands on the picker, clear the active restaurant.
  // This makes returning from a dashboard always show a "fresh" picker —
  // no pill, no Home/Users icons in the topbar — until they pick again.
  useEffect(() => {
    selectRestaurant(null);
    // We intentionally only run this once on mount.
    // eslint-disable-next-line
  }, []);

  const onSelect = (r) => {
    selectRestaurant(r);
    nav('/dashboard');
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <TopBar title="Choose Restaurant" />
      <div className="page" style={{ paddingTop: 40 }}>
        <div className="page-container">
          <div style={{ textAlign: 'center', marginBottom: 36 }}>
            <h1 className="page-title" style={{ fontSize: 30 }}>
              Welcome back, {user?.name?.split(' ')[0]} 👋
            </h1>
            <p className="page-subtitle">Pick a restaurant to manage</p>
          </div>

          {loading && <div className="empty"><div>Loading…</div></div>}

          {!loading && restaurants.length === 0 && (
            <div className="card" style={{ maxWidth: 540, margin: '0 auto', textAlign: 'center', padding: 40 }}>
              <div className="empty-icon">🏪</div>
              <div className="empty-title">No restaurants yet</div>
              <div className="empty-text" style={{ marginBottom: 24 }}>
                Add your first restaurant to start managing employees and shifts.
              </div>
              <button className="btn btn-primary" onClick={() => nav('/restaurants/manage')}>
                <HiOutlinePlusCircle size={18} /> Add your first restaurant
              </button>
            </div>
          )}

          {!loading && restaurants.length > 0 && (
            <>
              <div
                className="grid"
                style={{
                  gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
                  gap: 14,
                  maxWidth: 880,
                  margin: '0 auto'
                }}
              >
                {restaurants.map(r => (
                  <div
                    key={r._id}
                    className="rest-card"
                    onClick={() => onSelect(r)}
                  >
                    <div className="rest-card-logo">
                      {r.logo ? (
                        <img src={r.logo} alt={r.name} />
                      ) : (
                        <HiOutlineBuildingStorefront size={26} color="var(--ink-300)" />
                      )}
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3
                      }}>
                        <h3 style={{
                          margin: 0, fontSize: 18, fontWeight: 700,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          letterSpacing: '-0.01em'
                        }}>
                          {r.name}
                        </h3>
                        {!r.isActive && <span className="pill pill-warning">Inactive</span>}
                      </div>
                      {r.location && (
                        <p style={{
                          margin: 0, fontSize: 14, color: 'var(--text-muted)',
                          display: 'flex', alignItems: 'center', gap: 4,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                        }}>
                          <HiOutlineMapPin size={13} style={{ flexShrink: 0 }} /> {r.location}
                        </p>
                      )}
                    </div>

                    <HiOutlineArrowRight className="rest-card-arrow" size={18} />
                  </div>
                ))}
              </div>

              <div style={{ textAlign: 'center', marginTop: 36 }}>
                <button className="btn btn-ghost" onClick={() => nav('/restaurants/manage')}>
                  <HiOutlineCog6Tooth size={18} /> Manage restaurants
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        .rest-card {
          display: flex;
          align-items: center;
          gap: 18px;
          padding: 22px;
          background: var(--surface);
          border: 1.5px solid var(--border);
          border-radius: var(--r-md);
          cursor: pointer;
          transition: border-color 0.15s, transform 0.15s, box-shadow 0.15s;
          box-shadow: var(--shadow-xs);
        }
        .rest-card:hover {
          border-color: var(--blue-400);
          transform: translateY(-2px);
          box-shadow: var(--shadow-md);
        }
        .rest-card:hover .rest-card-arrow {
          color: var(--blue-500);
          transform: translateX(2px);
        }
        .rest-card-logo {
          width: 72px;
          height: 72px;
          border-radius: 14px;
          background: var(--paper-soft);
          border: 1px solid var(--border);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          overflow: hidden;
        }
        .rest-card-logo img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .rest-card-arrow {
          color: var(--text-light);
          flex-shrink: 0;
          transition: color 0.15s, transform 0.15s;
        }
        @media (max-width: 480px) {
          .rest-card { padding: 16px; gap: 14px; }
          .rest-card-logo { width: 60px; height: 60px; border-radius: 12px; }
        }
      `}</style>
    </div>
  );
};

export default RestaurantSelector;